import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Circle, Compass, Calendar } from "lucide-react";

const AUDIENCE_PILL = {
  "first-time-manager": "Manager",
  "senior-leader": "Senior Leader",
  "sales-rep": "Sales Rep",
  "customer-care": "Customer Care",
  "channel-partner-manager": "Channel Partner Manager",
};

export default function Journeys() {
  const navigate = useNavigate();
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSlug, setActiveSlug] = useState(null);
  const [startingScenarioId, setStartingScenarioId] = useState(null);

  useEffect(() => {
    api
      .get("/journeys")
      .then((res) => {
        setJourneys(res.data);
        if (res.data.length > 0) setActiveSlug(res.data[0].slug);
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const active = journeys.find((j) => j.slug === activeSlug);

  const startWeek = async (scenarioId) => {
    if (!scenarioId) return;
    setStartingScenarioId(scenarioId);
    try {
      const res = await api.post("/sessions", { scenario_id: scenarioId });
      navigate(`/session/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setStartingScenarioId(null);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
        <div className="text-xs tracking-[0.18em] uppercase text-stone-500 mb-3">Coaching journeys</div>
        <h1 className="font-display text-3xl md:text-4xl text-stone-900 tracking-tight">
          A curated 12-week path for every role.
        </h1>
        <p className="mt-2 text-stone-600 max-w-2xl">
          Pick a journey designed for your role — one scenario per week,
          building from foundations to the hardest conversations. Your
          progress is tracked automatically as you complete sessions.
        </p>

        {loading ? (
          <div className="mt-12 text-stone-500 text-sm">Loading journeys…</div>
        ) : (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="journeys-grid">
            {/* LEFT: Journey list */}
            <aside className="lg:col-span-4 flex flex-col gap-3">
              {journeys.map((j, i) => {
                const pct = j.total > 0 ? Math.round((j.completed_count / j.total) * 100) : 0;
                const isActive = j.slug === activeSlug;
                return (
                  <button
                    key={j.slug}
                    data-testid={`journey-${j.slug}-card`}
                    onClick={() => setActiveSlug(j.slug)}
                    className={`text-left bg-white rounded-3xl p-6 border transition-all slide-up-fade ${
                      isActive
                        ? "border-[#b81f24] shadow-[0_18px_50px_-24px_rgba(184,31,36,0.25)]"
                        : "border-stone-200 hover:border-stone-300"
                    }`}
                    style={{ animationDelay: `${i * 50}ms` }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] tracking-[0.18em] uppercase text-stone-500">
                        {AUDIENCE_PILL[j.audience] || j.audience}
                      </span>
                      <span className="text-xs text-stone-500">
                        {j.completed_count} / {j.total}
                      </span>
                    </div>
                    <div className="font-display text-lg text-stone-900 leading-tight mb-2">
                      {j.title}
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#b81f24] rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </button>
                );
              })}
            </aside>

            {/* RIGHT: Active journey detail */}
            <main className="lg:col-span-8">
              {active ? (
                <div className="bg-white border border-stone-200 rounded-3xl p-8">
                  <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
                    <div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500 mb-2 flex items-center gap-1.5">
                        <Compass className="w-3.5 h-3.5" strokeWidth={1.5} />
                        For {AUDIENCE_PILL[active.audience] || active.audience}
                      </div>
                      <h2 className="font-display text-2xl md:text-3xl text-stone-900 tracking-tight">
                        {active.title}
                      </h2>
                      <p className="mt-3 text-stone-600 max-w-2xl leading-relaxed">{active.description}</p>
                    </div>
                    <div className="bg-stone-900 text-stone-50 rounded-2xl px-4 py-3 shrink-0">
                      <div className="text-[10px] tracking-[0.18em] uppercase text-stone-400">Progress</div>
                      <div className="font-display text-2xl font-semibold mt-0.5">
                        {active.completed_count}<span className="text-stone-500 text-base"> / {active.total}</span>
                      </div>
                    </div>
                  </div>

                  <ol className="mt-2 space-y-3" data-testid="journey-weeks">
                    {active.weeks.map((w) => (
                      <li
                        key={w.week}
                        data-testid={`journey-week-${w.week}`}
                        className={`relative flex items-start gap-4 bg-stone-50 border rounded-2xl p-5 transition-all ${
                          w.completed ? "border-emerald-200 bg-emerald-50/40" : "border-stone-200"
                        }`}
                      >
                        <div className="shrink-0 mt-0.5">
                          {w.completed ? (
                            <CheckCircle2 className="w-6 h-6 text-emerald-600" strokeWidth={1.75} />
                          ) : (
                            <Circle className="w-6 h-6 text-stone-300" strokeWidth={1.5} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-stone-500 mb-1">
                            <Calendar className="w-3 h-3" strokeWidth={1.5} />
                            Week {w.week}
                            {w.scenario_category && (
                              <span className="text-stone-300">·</span>
                            )}
                            {w.scenario_category && <span>{w.scenario_category}</span>}
                          </div>
                          <div className="font-display text-base text-stone-900">{w.theme}</div>
                          {w.scenario_title && (
                            <div className="text-sm text-stone-600 mt-1 truncate">{w.scenario_title}</div>
                          )}
                          {w.completed && w.score != null && (
                            <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-emerald-700">
                              <span className="font-medium">Score: {w.score}</span>
                              <span className="text-stone-400">·</span>
                              <Link
                                to={`/history`}
                                className="hover:text-emerald-900 underline-offset-2 hover:underline"
                              >
                                Review session
                              </Link>
                            </div>
                          )}
                        </div>
                        <Button
                          data-testid={`journey-week-${w.week}-start-btn`}
                          disabled={!w.scenario_id || startingScenarioId === w.scenario_id}
                          onClick={() => startWeek(w.scenario_id)}
                          className={`rounded-md px-4 h-10 text-sm shrink-0 ${
                            w.completed
                              ? "bg-stone-100 text-stone-700 hover:bg-stone-200"
                              : "bg-[#b81f24] hover:bg-[#9a1a1f] text-white"
                          }`}
                        >
                          {startingScenarioId === w.scenario_id
                            ? "Starting…"
                            : w.completed
                              ? "Practice again"
                              : (<>Practice <ArrowUpRight className="w-4 h-4 ml-1" strokeWidth={1.75} /></>)}
                        </Button>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : (
                <div className="bg-white border border-dashed border-stone-200 rounded-3xl p-10 text-center text-stone-500">
                  Pick a journey on the left to see the 6-week plan.
                </div>
              )}
            </main>
          </div>
        )}
      </div>
    </div>
  );
}
